import { z } from 'zod'
import { currencySchema } from './money.ts'

/**
 * The numbers a mediator actually runs on. There is no payment gateway (D7), so
 * "revenue" here means orders locked, not cash collected — the labels say so.
 */

export const REPORT_RANGES = ['7d', '30d', '90d'] as const
export type ReportRange = (typeof REPORT_RANGES)[number]
export const reportRangeSchema = z.enum(REPORT_RANGES)

export const REPORT_RANGE_LABELS: Record<ReportRange, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
}

export const REPORT_RANGE_DAYS: Record<ReportRange, number> = { '7d': 7, '30d': 30, '90d': 90 }

export const topProductSchema = z.object({
  productId: z.string(),
  name: z.string(),
  unitCode: z.string(),
  qty: z.number().int(),
  revenueMinor: z.number().int(),
})
export type TopProduct = z.infer<typeof topProductSchema>

export const dashboardSchema = z.object({
  range: reportRangeSchema,
  currency: currencySchema,
  /** Requests still waiting on the desk. The one number worth interrupting for. */
  openRequests: z.number().int(),
  unansweredMessages: z.number().int(),
  pendingProposals: z.number().int(),
  lowStockCount: z.number().int(),
  ordersCount: z.number().int(),
  orderValueMinor: z.number().int(),
  marginMinor: z.number().int(),
  marginBps: z.number().int().nullable(),
  /** Accepted quotes over quotes sent, in basis points. */
  winRateBps: z.number().int().nullable(),
  /** Median time from request to first quote, in ms. */
  medianFirstResponseMs: z.number().int().nullable(),
  topProducts: z.array(topProductSchema),
})
export type Dashboard = z.infer<typeof dashboardSchema>

/**
 * What is sitting still. Capital tied up in the wrong item is the quietest way a
 * stockist loses money, and nothing else on the dashboard says it.
 */
export const stockAgingRowSchema = z.object({
  productId: z.string(),
  name: z.string(),
  unitCode: z.string(),
  onHandQty: z.number().int(),
  /** Whole days since the last movement. Null when it has never moved at all. */
  daysStill: z.number().int().nullable(),
})
export type StockAgingReportRow = z.infer<typeof stockAgingRowSchema>

/** Which shops are actually worth buying from. */
export const sellerScorecardSchema = z.object({
  orgId: z.string(),
  name: z.string(),
  ordersPlaced: z.number().int(),
  ordersConfirmed: z.number().int(),
  ordersDeclined: z.number().int(),
  ordersReceived: z.number().int(),
  valueMinor: z.number().int(),
  /** Mean ms from sending an order to the shop answering. Null with no answers. */
  meanResponseMs: z.number().int().nullable(),
  acceptRateBps: z.number().int().nullable(),
})
export type SellerScorecard = z.infer<typeof sellerScorecardSchema>
